import {errorToastOptions} from "../../components/ErrorToast";
import {
    IonButton, IonCard,
    IonCardHeader, IonCardSubtitle, IonCardTitle,
    IonContent, IonIcon, IonItem, IonLabel, IonList,
    IonRefresher,
    IonRefresherContent,
    IonSpinner, useIonAlert, useIonLoading, useIonRouter, useIonToast, useIonViewWillEnter
} from "@ionic/react";
import React, {useEffect, useState} from "react";
import {getUser, setEmail, setIsDisabled, setName} from "../../repositories/adminUserRepository";
import {keyOutline, peopleOutline, pencil} from "ionicons/icons";

export const AdminViewUser = ({userId}) => {
    const [user, setUser] = useState(null);
    const [presentAlert, dismissAlert] = useIonAlert();
    const [presentLoading, dismissLoading] = useIonLoading();
    const [presentToast] = useIonToast();

    const router = useIonRouter();

    const presentError = async (e, fallbackMessage) => {
        const message = typeof e.response?.data === 'string' && e.response.data ? e.response.data : fallbackMessage;
        await presentToast(errorToastOptions(message));
    }

    const loadUser = async () => {
        await presentLoading({message: 'Loading user...'});
        try {
            const userResponse = await getUser(userId);
            setUser(userResponse.user); // TODO: handle 404
        } catch (e) {
            await presentError(e, "Failed to load user");
        } finally {
            await dismissLoading();
        }
    }

    const showIsDisabledButton = () => {
        return <IonButton fill="clear" color={user.disabled ? 'success' : 'danger'}
                          onClick={async () => {
                              await presentLoading({message: (user.disabled ? 'Enabling' : 'Disabling') + ' user...'});
                              try {
                                  const newUser = await setIsDisabled(user.id, !user.disabled)
                                  setUser(newUser);
                              } catch (e) {
                                  await presentError(e, (user.disabled ? 'Failed to enable' : 'Failed to disable') + ' user');
                              } finally {
                                  await dismissLoading();
                              }
                          }}>
            {user.disabled ? 'Enable' : 'Disable'}
        </IonButton>
    }

    const editName = () => {
        presentAlert({
            cssClass: 'custom-alert',
            header: 'Edit name',
            inputs: [
                {
                    name: 'name',
                    type: 'text',
                    placeholder: 'Enter user name',
                    value: user.name
                }
            ],
            buttons: [
                { text: 'Cancel', role: 'cancel', handler: () => dismissAlert() },
                { text: 'Save',
                    handler: async (data) => {
                        await presentLoading({message: 'Setting user name...'});
                        try {
                            const newUser = await setName(user.id, data.name)
                            setUser(newUser);
                        } catch (e) {
                            await presentError(e, "Failed to set user name");
                        } finally {
                            await dismissLoading();
                        }
                    }
                }
            ]
        })
    }

    const editEmail = () => {
        presentAlert({
            cssClass: 'custom-alert',
            header: 'Edit email',
            inputs: [
                {
                    name: 'email',
                    type: 'text',
                    placeholder: 'Enter user email',
                    value: user.email
                }
            ],
            buttons: [
                { text: 'Cancel', role: 'cancel', handler: () => dismissAlert() },
                { text: 'Save',
                    handler: async (data) => {
                        await presentLoading({message: 'Setting user email...'});
                        try {
                            const newUser = await setEmail(user.id, data.email)
                            setUser(newUser);
                        } catch (e) {
                            await presentError(e, "Failed to set user email");
                        } finally {
                            await dismissLoading();
                        }
                    }
                }
            ]
        })
    }

    const reload = async (event) => {
        try {
            await loadUser();
        } finally {
            event.detail.complete();
        }
    }

    useIonViewWillEnter(() => {
        loadUser();
    }, [loadUser]);

    if (!user) {
        return <IonContent fullscreen>
            <div className="custom-spinner">
                <IonSpinner></IonSpinner>
            </div>
        </IonContent>
    }

    return <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={reload}>
            <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        <IonCard>
            <IonCardHeader>
                <IonCardTitle>{ user.name }
                    <IonButton onClick={editName} className="edit-button-icon" fill="clear" shape="round" size="small">
                        <IonIcon slot="icon-only" icon={pencil} />
                    </IonButton>
                </IonCardTitle>
                <IonCardSubtitle>{ user.email }
                    <IonButton onClick={editEmail} className="edit-button-icon" fill="clear" shape="round" size="small">
                        <IonIcon slot="icon-only" icon={pencil} onClick={editEmail} />
                    </IonButton>
                </IonCardSubtitle>
            </IonCardHeader>
            {showIsDisabledButton()}
        </IonCard>

        <IonList inset>
            <IonItem button detail onClick={() => router.push(`/admin/users/${user.id}/userNames`)}>
                <IonIcon slot="start" icon={keyOutline} />
                <IonLabel>View user names</IonLabel>
            </IonItem>
            <IonItem button detail lines="none" onClick={() => router.push(`/admin/users/${user.id}/managedAccesses`)}>
                <IonIcon slot="start" icon={peopleOutline} />
                <IonLabel>View user accesses</IonLabel>
            </IonItem>
        </IonList>
    </IonContent>
}